import { useState } from 'react';
import { useApp, Card, Icon, Pill, Button, Avatar, NakedButton, Field, Input, Select, Banner, Illustration } from '@/components/ds';
import { Segmented, Tracker, Code, CodeBlock, Spinner } from '@/components/code';
import { jsonStr } from '@/lib/codegen';
import { PAYMENT_STATES, fmtMoney, statusKind, statusLabel } from '@/data';
import { Drawer, Table, PageTitle } from '@/containers/dashboard';
import { useApiCtx, useAsync, Loading, ErrorBanner, PreviewNote } from '@/components/live';
import { ApiError } from '@/api/client';
import {
  ME,
  listBusinesses,
  getLimitations,
  patchBusiness,
  listFundingSources,
  createFundingSource,
  createDeliveryMethod,
  listPayments,
  createPayment,
  getPayment,
  createFundingSourceLink,
  type FundingSourceLinkType,
  type FundingSourceLink,
} from '@/api/payouts';

// dashboard2.tsx — Businesses, Payments, Funding sources, Delivery methods (live /v2 data).

// ---- display helpers ----------------------------------------------------
const DECO = ['var(--decorative-2)', 'var(--decorative-4)', 'var(--decorative-5)', 'var(--decorative-3)', 'var(--decorative-1)'];
const bizName = (b: any): string => b?.name || b?.legalName || b?.org?.name || b?.org?.legalName || b?.id || 'Business';
const bizInitials = (b: any): string =>
  bizName(b)
    .split(/\s+/)
    .slice(0, 2)
    .map((w: string) => w[0])
    .join('')
    .toUpperCase();
const bizColor = (b: any): string => DECO[[...(b?.id || '')].reduce((a: number, c: string) => a + c.charCodeAt(0), 0) % DECO.length];
// Returns 'eligible' | 'not-eligible' | 'unknown' — unknown when the list endpoint omits eligibility.
const eligibilityState = (b: any): 'eligible' | 'not-eligible' | 'unknown' => {
  if (!b?.eligibility) return 'unknown';
  return b.eligibility.canMakePayments ? 'eligible' : 'not-eligible';
};
const isEligible = (b: any): boolean => eligibilityState(b) === 'eligible';

// ====== BUSINESSES =======================================================
interface EligibilityCardProps {
  ctx: any;
  businessId: string;
  style?: React.CSSProperties;
}

function EligibilityCard({ ctx, businessId }: EligibilityCardProps) {
  const { loading, data, error, reload } = useAsync(
    (signal: AbortSignal) => getLimitations(ctx, businessId, signal),
    [ctx.env, ctx.apiKey, businessId]
  );
  const [fixOpen, setFixOpen] = useState(false);
  const [dob, setDob] = useState('');
  const [ein, setEin] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');

  if (loading) return <Loading label="Checking eligibility…" />;
  if (error) return <ErrorBanner error={error} onRetry={reload} />;

  const eligible = !!data?.canMakePayments;
  const missing: string[] = data?.missingFields || [];

  const submit = async () => {
    setSaving(true);
    setSaveErr('');
    const patch: any = {};
    if (dob) patch.owner = { dateOfBirth: dob };
    if (ein) patch.taxInfo = { type: 'ein', identifier: ein };
    try {
      await patchBusiness(ctx, businessId, patch);
      setFixOpen(false);
      setDob('');
      setEin('');
      reload();
    } catch (ex: any) {
      setSaveErr(ex?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card padding={22}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: eligible ? 'var(--success-100)' : 'var(--warning-100)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon
            name={eligible ? 'checked-circle-fill' : 'warning'}
            size={22}
            color={eligible ? 'var(--success-700)' : 'var(--warning-700)'}
          />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>{eligible ? 'Eligible to make payments' : 'Not eligible yet'}</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {eligible ? 'All KYC checks passed.' : `${missing.length} field${missing.length === 1 ? '' : 's'} missing.`}
          </div>
        </div>
        <code className="om-mono" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          canMakePayments: {String(eligible)}
        </code>
      </div>
      {!eligible && (
        <div>
          {(data?.blockingReasons || []).length > 0 && (
            <Banner kind="warning" style={{ marginBottom: 12 }}>
              Blocking: {data?.blockingReasons.join(', ')}
            </Banner>
          )}
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Missing fields</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {missing.map((m) => (
              <div
                key={m}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 12px',
                  border: '1px solid var(--border-static)',
                  borderRadius: 8,
                }}
              >
                <Icon name="error" size={16} color="var(--warning-700)" />
                <code className="om-mono" style={{ flex: 1, fontSize: 13 }}>
                  {m}
                </code>
                <Pill kind="warning" dot={false}>
                  required
                </Pill>
              </div>
            ))}
          </div>
          <Button variant="secondary" style={{ marginTop: 14 }} onClick={() => setFixOpen(true)} leftIcon="edit">
            Complete profile
          </Button>
        </div>
      )}

      <Drawer
        open={fixOpen}
        onClose={() => setFixOpen(false)}
        title="Complete KYC"
        footer={
          <>
            <Button variant="tertiary" onClick={() => setFixOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={saving}>
              {saving ? <Spinner size={16} color="#fff" /> : 'Submit & re-check'}
            </Button>
          </>
        }
      >
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 0 }}>
          These map to the <Code>missingFields</Code> above. Submitting issues a <Code>PATCH /v2/entities/{businessId}</Code>.
        </p>
        {saveErr && (
          <Banner kind="critical" style={{ marginBottom: 12 }}>
            {saveErr}
          </Banner>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18, marginTop: 18 }}>
          <Field label="Owner date of birth" helper="owner.dateOfBirth">
            <Input value={dob} onChange={setDob} placeholder="1985-09-22" mono />
          </Field>
          <Field label="Tax ID (EIN)" helper="taxInfo.identifier">
            <Input value={ein} onChange={setEin} placeholder="47-2913084" mono />
          </Field>
        </div>
      </Drawer>
    </Card>
  );
}

interface DefListProps {
  rows: [string, React.ReactNode][];
  style?: React.CSSProperties;
}

function DefList({ rows }: DefListProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {rows.map(([k, v]) => (
        <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
          <span style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>{k}</span>
          <span style={{ fontSize: 13.5, fontWeight: 600, textAlign: 'right' }}>{v}</span>
        </div>
      ))}
    </div>
  );
}

interface BusinessDetailProps {
  business: any;
  onBack?: () => void;
  trusted?: boolean;
  style?: React.CSSProperties;
}

function BusinessDetail({ business, onBack, trusted }: BusinessDetailProps) {
  const ctx = useApiCtx();
  const [tab, setTab] = useState('eligibility');
  const businessId = trusted ? business.id : ME;
  // API returns a flat shape; fall back to business itself when the nested org wrapper is absent.
  const org = business.org || business;
  const owner = business.owner || {};
  const tabs: [string, string][] = [
    ['profile', 'Profile & KYC'],
    ['eligibility', 'Eligibility'],
    ['funding', 'Funding sources'],
    ['delivery', 'Delivery methods'],
    ['payments', 'Payments'],
    ['links', 'Account links'],
  ];
  return (
    <div>
      {trusted && (
        <NakedButton leftIcon="arrow-left" color="var(--text-secondary)" onClick={onBack} style={{ marginBottom: 14 }}>
          All businesses
        </NakedButton>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
        <Avatar initials={bizInitials(business)} color={bizColor(business)} size={48} />
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 style={{ margin: 0, fontSize: 24, letterSpacing: '-0.01em' }}>{bizName(business)}</h1>
            {eligibilityState(business) !== 'unknown' && (
              <Pill kind={isEligible(business) ? 'success' : 'warning'}>{isEligible(business) ? 'Eligible' : 'Action needed'}</Pill>
            )}
          </div>
          <code className="om-mono" style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
            {business.id}
          </code>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border-static)', marginBottom: 22 }}>
        {tabs.map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            style={{
              padding: '10px 14px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600,
              color: tab === id ? 'var(--text-primary)' : 'var(--text-secondary)',
              borderBottom: `2px solid ${tab === id ? 'var(--accent)' : 'transparent'}`,
              marginBottom: -1,
            }}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === 'eligibility' && <EligibilityCard ctx={ctx} businessId={businessId} />}
      {tab === 'profile' && (
        <div className="grid-2">
          <Card padding={22}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--text-secondary)',
                marginBottom: 14,
                textTransform: 'uppercase',
                letterSpacing: '.03em',
              }}
            >
              Organization
            </div>
            <DefList
              rows={[
                ['Legal name', org.legalName || org.name || '—'],
                ['Business type', org.businessType || '—'],
                ['Tax ID', org.taxInfo?.identifierLast4 ? `••••${org.taxInfo.identifierLast4}` : '—'],
                ['Industry', org.industry?.name || org.industry?.naicsCode || '—'],
                ['Website', org.website || '—'],
              ]}
            />
          </Card>
          <Card padding={22}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--text-secondary)',
                marginBottom: 14,
                textTransform: 'uppercase',
                letterSpacing: '.03em',
              }}
            >
              Owner
            </div>
            <DefList
              rows={[
                ['Name', [owner.firstName, owner.lastName].filter(Boolean).join(' ') || '—'],
                ['Email', owner.email || '—'],
              ]}
            />
          </Card>
        </div>
      )}
      {tab === 'funding' && <FundingView embedded businessId={businessId} />}
      {tab === 'delivery' && <DeliveryView embedded businessId={businessId} />}
      {tab === 'payments' && <PaymentsView embedded businessId={businessId} />}
      {tab === 'links' && <FundingLinkView businessId={businessId} />}
    </div>
  );
}

interface BusinessesViewProps {
  trusted?: boolean;
  style?: React.CSSProperties;
  biz?: any;
  setBiz?: React.Dispatch<React.SetStateAction<any>>;
  setView?: (view: string) => void;
}

function BusinessesView({ trusted }: BusinessesViewProps) {
  const ctx = useApiCtx();
  const [detail, setDetail] = useState<any>(null);
  const { loading, data, error, reload } = useAsync((signal: AbortSignal) => listBusinesses(ctx, signal), [ctx.env, ctx.apiKey]);

  if (loading) return <Loading label="Loading businesses…" />;
  if (error) return <ErrorBanner error={error} onRetry={reload} />;
  const businesses: any[] = data || [];

  // Self-serve partners have exactly one business → straight to its detail (me sentinel).
  if (!trusted) {
    const only = businesses[0] || { id: ME, org: {} };
    return <BusinessDetail business={only} trusted={false} />;
  }
  if (detail) return <BusinessDetail business={detail} trusted onBack={() => setDetail(null)} />;
  return (
    <div>
      <PageTitle
        title="Businesses"
        sub="Every legal entity you onboard and pay from."
        action={
          <Button leftIcon="add" disabled>
            Add business
          </Button>
        }
      />
      {businesses.length === 0 ? (
        <Card padding={32} style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
          No businesses on this partner account yet.
        </Card>
      ) : (
        <Table
          onRow={(r: any) => setDetail(r)}
          columns={[
            {
              key: 'name',
              label: 'Business',
              render: (r: any) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Avatar initials={bizInitials(r)} color={bizColor(r)} size={30} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{bizName(r)}</div>
                    <code className="om-mono" style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                      {r.id}
                    </code>
                  </div>
                </div>
              ),
            },
            {
              key: 'eligible',
              label: 'Eligibility',
              render: (r: any) => {
                const state = eligibilityState(r);
                if (state === 'unknown') return <Pill kind="neutral">Unknown</Pill>;
                return (
                  <Pill kind={state === 'eligible' ? 'success' : 'warning'}>{state === 'eligible' ? 'Eligible' : 'Action needed'}</Pill>
                );
              },
            },
            { key: 'x', label: '', align: 'right', render: () => <Icon name="chevron-right" size={16} color="var(--neutral-600)" /> },
          ]}
          rows={businesses}
        />
      )}
    </div>
  );
}

// ====== PAYMENTS =========================================================
const PAY_STATE_ORDER = ['scheduled', 'in-progress', 'completed'];

interface PaymentTrackerProps {
  payment: any;
  style?: React.CSSProperties;
}

function PaymentTracker({ payment }: PaymentTrackerProps) {
  const idx = PAY_STATE_ORDER.indexOf(payment.status === 'failed' ? 'in-progress' : payment.status);
  if (payment.status === 'failed' || payment.status === 'canceled') {
    return (
      <Banner kind="critical">
        Payment {statusLabel(payment.status)}. {payment.failureReason || ''}
      </Banner>
    );
  }
  return <Tracker steps={PAYMENT_STATES.map((s: any) => ({ label: s.label }))} current={idx < 0 ? 0 : idx} />;
}

interface PaymentDetailProps {
  ctx: any;
  businessId: string;
  payment: any;
  style?: React.CSSProperties;
}

function PaymentDetail({ ctx, businessId, payment: initial }: PaymentDetailProps) {
  // Refetch so the tracker reflects current status (ACH/check are async).
  const { loading, data, error, reload } = useAsync(
    (signal: AbortSignal) => getPayment(ctx, businessId, initial.id, signal),
    [ctx.env, ctx.apiKey, businessId, initial.id]
  );
  const payment = data || initial;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
        <span style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.02em' }} className="om-mono">
          {fmtMoney(payment.amount)}
        </span>
        <Pill kind={statusKind(payment.status)}>{statusLabel(payment.status)}</Pill>
        <div style={{ flex: 1 }} />
        <NakedButton onClick={reload}>Refresh</NakedButton>
      </div>
      <code className="om-mono" style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
        {payment.id}
      </code>
      <div style={{ margin: '24px 0', padding: '22px 18px', border: '1px solid var(--border-static)', borderRadius: 12 }}>
        {loading ? <Loading pad={16} /> : error ? <ErrorBanner error={error} onRetry={reload} /> : <PaymentTracker payment={payment} />}
      </div>
      <CodeBlock title={`GET …/payments/${payment.id}`} code={jsonStr(payment)} maxHeight={260} />
    </div>
  );
}

interface CreatePaymentFormProps {
  ctx: any;
  businessId: string;
  onCreated?: (payment: any, done?: boolean) => void;
  style?: React.CSSProperties;
}

function CreatePaymentForm({ ctx, businessId, onCreated }: CreatePaymentFormProps) {
  const fsList = useAsync((signal: AbortSignal) => listFundingSources(ctx, businessId, signal), [ctx.env, ctx.apiKey, businessId]);
  const [amount, setAmount] = useState('1250.00');
  const [fs, setFs] = useState('');
  const [dm, setDm] = useState('');
  const [date, setDate] = useState('');
  const [pref, setPref] = useState('standard-ach');
  const [note, setNote] = useState('');
  const [stage, setStage] = useState<'form' | 'sending' | 'done'>('form'); // form | sending | done
  const [created, setCreated] = useState<any>(null);
  const [err, setErr] = useState<any>(null);

  const fsOptions: { value: string; label: string }[] = (fsList.data || []).map((f: any) => ({
    value: f.id,
    label: `${f.nickname || f.id}${f.bankAccount?.accountNumberLast4 ? ` ••${f.bankAccount.accountNumberLast4}` : ''}${f.isVerified ? ' · verified' : ''}`,
  }));
  const fsValue = fs || (fsOptions[0] ? fsOptions[0].value : '');

  const body = {
    amount: Math.round(parseFloat(amount || '0') * 100),
    originatingAccountId: fsValue,
    receivingAccountId: dm,
    deductionDate: date,
    deliveryPreference: pref,
    ...(note ? { noteToRecipient: note } : {}),
  };

  const submit = async () => {
    setErr(null);
    setStage('sending');
    try {
      const res = await createPayment(ctx, businessId, body);
      setCreated(res);
      setStage('done');
      onCreated && onCreated(res);
    } catch (ex: any) {
      setErr(ex);
      setStage('form');
    }
  };

  if (stage === 'done' && created) {
    return (
      <div style={{ textAlign: 'center', animation: 'om-fade-up .3s ease' }}>
        <Illustration name="money-success" size={120} style={{ margin: '0 auto 10px' }} />
        <h3 style={{ margin: 0, fontSize: 22 }}>Payment {statusLabel(created.status || 'scheduled')}</h3>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: '8px 0 20px' }}>
          {fmtMoney(created.amount)} · {created.id}
        </p>
        <div style={{ padding: '18px 16px', border: '1px solid var(--border-static)', borderRadius: 12, marginBottom: 18 }}>
          <PaymentTracker payment={created} />
        </div>
        <Button fullWidth onClick={() => onCreated && onCreated(created, true)}>
          Done
        </Button>
      </div>
    );
  }

  // 409 BUSINESS_NOT_ELIGIBLE → surface the missing fields inline (tech-design §6.3).
  const eligibilityErr = err instanceof ApiError && err.status === 409 ? err.body?.error?.details : null;

  return (
    <div>
      {err && !eligibilityErr && (
        <Banner kind="critical" style={{ marginBottom: 16 }}>
          {err.message}
        </Banner>
      )}
      {eligibilityErr && (
        <Banner kind="warning" style={{ marginBottom: 16 }}>
          <strong>Business not eligible.</strong> Missing: {((eligibilityErr.missingFields as string[]) || []).join(', ') || '—'}. Resolve
          KYC on the Eligibility tab first.
        </Banner>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <Field label="Amount" required helper="Sent to the API as integer cents.">
          <Input prefix="$" value={amount} onChange={setAmount} mono />
        </Field>
        <Field label="Funding source" required helper={fsList.error ? 'Could not load funding sources' : undefined}>
          {fsList.loading ? (
            <Loading pad={8} label="Loading…" />
          ) : (
            <Select
              value={fsValue}
              onChange={setFs}
              options={fsOptions.length ? fsOptions : [{ value: '', label: 'No funding sources — add one first' }]}
            />
          )}
        </Field>
        <Field
          label="Receiving account id"
          required
          helper="acct_… (no list endpoint yet — paste the delivery-method account id you created)."
        >
          <Input value={dm} onChange={setDm} placeholder="acct_7c4b1" mono />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="Scheduled date" required>
            <Input value={date} onChange={setDate} placeholder="2026-06-10" mono />
          </Field>
          <Field label="Delivery speed">
            <Select
              value={pref}
              onChange={setPref}
              options={[
                { value: 'standard-ach', label: 'Standard ACH (free)' },
                { value: 'same-day-ach', label: 'Same-day ACH' },
                { value: 'rtp', label: 'Real-time payment' },
                { value: 'standard-check', label: 'Standard check' },
                { value: 'express-check', label: 'Express check' },
                { value: 'overnight-check', label: 'Overnight check' },
                { value: 'domestic-wire', label: 'Domestic wire' },
                { value: 'instant-domestic-wire', label: 'Instant domestic wire' },
                { value: 'virtual-card', label: 'Virtual card' },
                { value: 'instant-virtual-card', label: 'Instant virtual card' },
              ]}
            />
          </Field>
        </div>
        <Field label="Note" helper="Shown to the payee on checks.">
          <Input value={note} onChange={setNote} />
        </Field>
      </div>
      <div style={{ marginTop: 18 }}>
        <CodeBlock dark title="POST /v2/payments (Melio-Entity-Id header)" maxHeight={180} code={jsonStr(body)} />
      </div>
      <Button
        fullWidth
        size="large"
        style={{ marginTop: 18 }}
        onClick={submit}
        disabled={stage === 'sending'}
        leftIcon={stage === 'sending' ? undefined : 'pay'}
      >
        {stage === 'sending' ? <Spinner size={16} color="#fff" /> : 'Schedule payment'}
      </Button>
    </div>
  );
}

interface PaymentsViewProps {
  embedded?: boolean;
  businessId?: string;
  style?: React.CSSProperties;
}

function PaymentsView({ embedded, businessId: propId }: PaymentsViewProps) {
  const ctx = useApiCtx();
  const businessId = propId || ME;
  const [create, setCreate] = useState(false);
  const [detail, setDetail] = useState<any>(null);
  const { loading, data, error, reload } = useAsync(
    (signal: AbortSignal) => listPayments(ctx, businessId, signal),
    [ctx.env, ctx.apiKey, businessId]
  );
  const payments: any[] = data || [];

  return (
    <div>
      {!embedded && <PageTitle title="Payments overview" sub="Track payouts and their lifecycle." />}
      {embedded && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <Button variant="secondary" size="small" leftIcon="add" onClick={() => setCreate(true)}>
            Create payment
          </Button>
        </div>
      )}
      {loading ? (
        <Loading label="Loading payments…" />
      ) : error ? (
        <ErrorBanner error={error} onRetry={reload} />
      ) : payments.length === 0 ? (
        <Card padding={32} style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
          No payments yet.
        </Card>
      ) : (
        <Table columns={paymentColumns()} rows={payments} onRow={(r: any) => setDetail(r)} />
      )}
      <Drawer open={create} onClose={() => setCreate(false)} title="Create a payment">
        <CreatePaymentForm
          ctx={ctx}
          businessId={businessId}
          onCreated={(_p: any, done?: boolean) => {
            if (done) {
              setCreate(false);
              reload();
            }
          }}
        />
      </Drawer>
      <Drawer open={!!detail} onClose={() => setDetail(null)} title="Payment">
        {detail && <PaymentDetail ctx={ctx} businessId={businessId} payment={detail} />}
      </Drawer>
    </div>
  );
}

function paymentColumns(): any[] {
  return [
    {
      key: 'id',
      label: 'Payment',
      render: (r: any) => (
        <code className="om-mono" style={{ fontSize: 12, color: 'var(--text-primary)' }}>
          {r.id}
        </code>
      ),
    },
    {
      key: 'originatingAccountId',
      label: 'From',
      render: (r: any) => (
        <code className="om-mono" style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          {r.originatingAccountId || '—'}
        </code>
      ),
    },
    {
      key: 'receivingAccountId',
      label: 'To',
      render: (r: any) => (
        <code className="om-mono" style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          {r.receivingAccountId || '—'}
        </code>
      ),
    },
    {
      key: 'method',
      label: 'Delivery',
      render: (r: any) => (
        <Pill kind="neutral" dot={false}>
          {r.deliveryPreference || 'standard-ach'}
        </Pill>
      ),
    },
    {
      key: 'date',
      label: 'Deduction date',
      render: (r: any) => (
        <span className="om-mono" style={{ fontSize: 13 }}>
          {r.deductionDate || '—'}
        </span>
      ),
    },
    {
      key: 'amount',
      label: 'Amount',
      align: 'right',
      render: (r: any) => (
        <span className="om-mono" style={{ fontWeight: 600 }}>
          {fmtMoney(r.amount)}
        </span>
      ),
    },
    { key: 'status', label: 'Status', render: (r: any) => <Pill kind={statusKind(r.status)}>{statusLabel(r.status)}</Pill> },
  ];
}

// ====== FUNDING SOURCES ==================================================
interface FundingViewProps {
  embedded?: boolean;
  businessId?: string;
  style?: React.CSSProperties;
}

function FundingView({ embedded, businessId: propId }: FundingViewProps) {
  const ctx = useApiCtx();
  const businessId = propId || ME;
  const [add, setAdd] = useState(false);
  const { loading, data, error, reload } = useAsync(
    (signal: AbortSignal) => listFundingSources(ctx, businessId, signal),
    [ctx.env, ctx.apiKey, businessId]
  );

  const [nickname, setNickname] = useState('');
  const [accountType, setAccountType] = useState('checking');
  const [routingNumber, setRouting] = useState('');
  const [accountNumber, setAccount] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');

  const submit = async () => {
    setSaving(true);
    setSaveErr('');
    try {
      await createFundingSource(ctx, businessId, {
        type: 'ach',
        nickname: nickname || undefined,
        bankAccount: { accountType, routingNumber, accountNumber },
      });
      setAdd(false);
      setNickname('');
      setRouting('');
      setAccount('');
      reload();
    } catch (ex: any) {
      setSaveErr(ex?.message || 'Failed to add');
    } finally {
      setSaving(false);
    }
  };

  const sources: any[] = data || [];
  return (
    <div>
      {!embedded && (
        <PageTitle
          title="Funding sources"
          sub="Bank accounts you debit to fund payments."
          action={
            <Button leftIcon="add" onClick={() => setAdd(true)}>
              Add funding source
            </Button>
          }
        />
      )}
      {embedded && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <Button variant="secondary" size="small" leftIcon="add" onClick={() => setAdd(true)}>
            Add funding source
          </Button>
        </div>
      )}
      {loading ? (
        <Loading label="Loading funding sources…" />
      ) : error ? (
        <ErrorBanner error={error} onRetry={reload} />
      ) : sources.length === 0 ? (
        <Card padding={32} style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
          No funding sources yet.
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {sources.map((s: any) => (
            <Card key={s.id} padding={18} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 10,
                  background: 'var(--accent-soft)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon name="bank" size={22} color="var(--accent)" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{s.nickname || 'Bank account'}</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }} className="om-mono">
                  ACH · {s.bankAccount?.accountType || 'account'} ••{s.bankAccount?.accountNumberLast4 || '----'}
                </div>
              </div>
              <Pill kind={s.isVerified ? 'success' : 'warning'}>{s.isVerified ? 'Verified' : 'Verifying'}</Pill>
              <code className="om-mono" style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                {s.id}
              </code>
            </Card>
          ))}
        </div>
      )}
      <Drawer
        open={add}
        onClose={() => setAdd(false)}
        title="Add a funding source"
        footer={
          <>
            <Button variant="tertiary" onClick={() => setAdd(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={saving}>
              {saving ? <Spinner size={16} color="#fff" /> : 'Add account'}
            </Button>
          </>
        }
      >
        <Banner kind="brand" style={{ marginBottom: 18 }}>
          Account numbers are encrypted on receipt. The API only ever returns the last 4 digits.
        </Banner>
        {saveErr && (
          <Banner kind="critical" style={{ marginBottom: 12 }}>
            {saveErr}
          </Banner>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Field label="Nickname">
            <Input value={nickname} onChange={setNickname} placeholder="Operating account" />
          </Field>
          <Field label="Account type" required>
            <Select
              value={accountType}
              onChange={setAccountType}
              options={[
                { value: 'checking', label: 'Checking' },
                { value: 'savings', label: 'Savings' },
              ]}
            />
          </Field>
          <Field label="Routing number" required>
            <Input value={routingNumber} onChange={setRouting} placeholder="021000021" mono />
          </Field>
          <Field label="Account number" required>
            <Input value={accountNumber} onChange={setAccount} placeholder="000123456789" mono />
          </Field>
        </div>
      </Drawer>
    </div>
  );
}

// ====== DELIVERY METHODS =================================================
interface DeliveryViewProps {
  embedded?: boolean;
  businessId?: string;
  style?: React.CSSProperties;
}

interface CreatedDeliveryMethod {
  id: string;
  name: string;
  type: string;
  detail: string;
}

function DeliveryView({ embedded, businessId: propId }: DeliveryViewProps) {
  const ctx = useApiCtx();
  const businessId = propId || ME;
  const [add, setAdd] = useState(false);
  const [type, setType] = useState('ach');
  // No list endpoint → show what was created this session so ids are copyable for payments.
  const [created, setCreated] = useState<CreatedDeliveryMethod[]>([]);

  const [payeeName, setPayee] = useState('');
  const [routingNumber, setRouting] = useState('');
  const [accountNumber, setAccount] = useState('');
  const [printName, setPrintName] = useState('');
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [aptNumber, setAptNumber] = useState('');
  const [city, setCity] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [zip, setZip] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');

  const submit = async () => {
    setSaving(true);
    setSaveErr('');
    const body: any =
      type === 'ach'
        ? { type: 'ach', counterPartyName: payeeName, bankAccount: { accountType: 'checking', routingNumber, accountNumber } }
        : {
            type: 'check',
            counterPartyName: payeeName,
            paperCheck: {
              printName: printName || payeeName,
              address: {
                line1,
                ...(line2 ? { line2 } : {}),
                city,
                state: stateCode,
                postalCode: zip,
                ...(aptNumber ? { aptNumber } : {}),
                countryCode: 'US',
              },
            },
          };
    try {
      const res = await createDeliveryMethod(ctx, businessId, body);
      setCreated((c) => [
        {
          id: res.id,
          name: res.counterPartyName || payeeName,
          type,
          detail: type === 'ach' ? `ACH ••${res.bankAccount?.accountNumberLast4 || '----'}` : `${line1}, ${city}, ${stateCode}`,
        },
        ...c,
      ]);
      setAdd(false);
      setPayee('');
      setRouting('');
      setAccount('');
      setLine1('');
      setLine2('');
      setAptNumber('');
      setCity('');
      setStateCode('');
      setZip('');
    } catch (ex: any) {
      setSaveErr(ex?.message || 'Failed to add');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {!embedded && (
        <PageTitle
          title="Delivery methods"
          sub="Where payments are sent — ACH or a mailed check."
          action={
            <Button leftIcon="add" onClick={() => setAdd(true)}>
              Add delivery method
            </Button>
          }
        />
      )}
      {embedded && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <Button variant="secondary" size="small" leftIcon="add" onClick={() => setAdd(true)}>
            Add delivery method
          </Button>
        </div>
      )}
      <PreviewNote>
        This demo lists only the delivery methods created in this session (partner-key delivery-method listing isn't wired up here).
        Creating one is live.
      </PreviewNote>
      {created.length === 0 ? (
        <Card padding={32} style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
          No delivery methods added in this session yet.
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {created.map((m) => (
            <Card key={m.id} padding={18} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 10,
                  background: 'var(--accent-soft)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon name={m.type === 'ach' ? 'bank' : 'paper-check'} size={22} color="var(--accent)" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{m.name}</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{m.detail}</div>
              </div>
              <Pill kind="neutral" dot={false}>
                {m.type === 'ach' ? 'ACH' : 'Paper check'}
              </Pill>
              <code className="om-mono" style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                {m.id}
              </code>
            </Card>
          ))}
        </div>
      )}
      <Drawer
        open={add}
        onClose={() => setAdd(false)}
        title="Add a delivery method"
        footer={
          <>
            <Button variant="tertiary" onClick={() => setAdd(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={saving}>
              {saving ? <Spinner size={16} color="#fff" /> : 'Add method'}
            </Button>
          </>
        }
      >
        {saveErr && (
          <Banner kind="critical" style={{ marginBottom: 12 }}>
            {saveErr}
          </Banner>
        )}
        <div style={{ marginBottom: 18 }}>
          <Field label="Delivery type">
            <Segmented
              value={type}
              onChange={setType}
              options={[
                { value: 'ach', label: 'ACH transfer', icon: 'bank' },
                { value: 'check', label: 'Paper check', icon: 'paper-check' },
              ]}
            />
          </Field>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Field label="Payee name" required>
            <Input value={payeeName} onChange={setPayee} placeholder="Flour Power Supply Co." />
          </Field>
          {type === 'ach' ? (
            <>
              <Field label="Routing number" required>
                <Input value={routingNumber} onChange={setRouting} placeholder="110000000" mono />
              </Field>
              <Field label="Account number" required>
                <Input value={accountNumber} onChange={setAccount} placeholder="000987654432" mono />
              </Field>
            </>
          ) : (
            <>
              <Field label="Print name" required>
                <Input value={printName} onChange={setPrintName} placeholder="Flour Power Supply Co." />
              </Field>
              <Field label="Street address" required>
                <Input value={line1} onChange={setLine1} placeholder="88 Market St" />
              </Field>
              <Field label="Address line 2" helper="Optional — suite, floor, etc.">
                <Input value={line2} onChange={setLine2} placeholder="Suite 400" />
              </Field>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
                <Field label="City" required>
                  <Input value={city} onChange={setCity} placeholder="San Francisco" />
                </Field>
                <Field label="State" required>
                  <Input value={stateCode} onChange={setStateCode} placeholder="CA" />
                </Field>
                <Field label="ZIP" required>
                  <Input value={zip} onChange={setZip} placeholder="94103" mono />
                </Field>
              </div>
              <Field label="Apt / suite number" helper="Optional.">
                <Input value={aptNumber} onChange={setAptNumber} placeholder="Apt 12" />
              </Field>
              <Banner kind="brand">Melio prints and mails the check on your behalf — no printer or stamps needed.</Banner>
            </>
          )}
        </div>
      </Drawer>
    </div>
  );
}

// ====== FUNDING SOURCE LINKS =============================================
interface FundingLinkViewProps {
  businessId?: string;
}

interface GeneratedLink {
  type: FundingSourceLinkType;
  data: FundingSourceLink;
  createdAt: string;
}

function FundingLinkView({ businessId: propId }: FundingLinkViewProps) {
  const ctx = useApiCtx();
  const businessId = propId || ME;
  const [type, setType] = useState<FundingSourceLinkType>('plaid');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [links, setLinks] = useState<GeneratedLink[]>([]);
  const [copied, setCopied] = useState<string | null>(null);

  const generate = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await createFundingSourceLink(ctx, businessId, type);
      setLinks((prev) => [{ type, data, createdAt: new Date().toISOString() }, ...prev]);
    } catch (ex: any) {
      setError(ex?.message || 'Failed to generate link');
    } finally {
      setLoading(false);
    }
  };

  const copyText = (text: string, key: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  // The shareable URL is what the partner sends to the business; opening it lands on the
  // portal's add-funding-source flow (the token is also embedded in the URL).
  const linkValue = (data: FundingSourceLink): string | null => data.url ?? null;

  return (
    <div>
      <PageTitle
        title="Account links"
        sub="Generate a hosted flow link to connect a Plaid bank account (funding source) or a card via Basis Theory."
      />
      <Card padding={22} style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end' }}>
          <Field label="Link type" style={{ flex: 1 }}>
            <Select
              value={type}
              onChange={(v) => setType(v as FundingSourceLinkType)}
              options={[
                { value: 'plaid', label: 'Plaid (bank account / funding source)' },
                { value: 'card', label: 'Card — Basis Theory (funding source)' },
              ]}
            />
          </Field>
          <Button onClick={generate} disabled={loading} leftIcon="add">
            {loading ? <Spinner size={16} color="#fff" /> : 'Generate link'}
          </Button>
        </div>
        {error && (
          <Banner kind="critical" style={{ marginTop: 14 }}>
            {error}
          </Banner>
        )}
      </Card>

      {links.length === 0 ? (
        <Card padding={32} style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
          No links generated yet — select a type and click Generate.
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {links.map((item, i) => {
            const val = linkValue(item.data);
            const copyKey = `${i}-val`;
            const copyJsonKey = `${i}-json`;
            return (
              <Card key={i} padding={18}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      background: 'var(--accent-soft)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon name="bank" size={22} color="var(--accent)" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>{item.type === 'plaid' ? 'Plaid link' : 'Card link (Basis Theory)'}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{new Date(item.createdAt).toLocaleTimeString()}</div>
                  </div>
                  <Pill kind="brand">{item.type}</Pill>
                </div>

                {val && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
                      Shareable link — send this to the business
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '10px 12px',
                        background: 'var(--surface-secondary)',
                        borderRadius: 8,
                        border: '1px solid var(--border-static)',
                      }}
                    >
                      <code className="om-mono" style={{ flex: 1, fontSize: 12, wordBreak: 'break-all', color: 'var(--text-primary)' }}>
                        {val}
                      </code>
                      <Button
                        variant="tertiary"
                        size="small"
                        leftIcon={copied === copyKey ? 'checked-circle-fill' : 'copy'}
                        onClick={() => copyText(val, copyKey)}
                      >
                        {copied === copyKey ? 'Copied' : 'Copy'}
                      </Button>
                    </div>
                  </div>
                )}

                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Full response</div>
                  <div style={{ position: 'relative' }}>
                    <CodeBlock code={JSON.stringify(item.data, null, 2)} lang="json" />
                    <Button
                      variant="tertiary"
                      size="small"
                      leftIcon={copied === copyJsonKey ? 'checked-circle-fill' : 'copy'}
                      onClick={() => copyText(JSON.stringify(item.data, null, 2), copyJsonKey)}
                      style={{ position: 'absolute', top: 8, right: 8 }}
                    >
                      {copied === copyJsonKey ? 'Copied' : 'Copy JSON'}
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

export {
  BusinessesView,
  BusinessDetail,
  EligibilityCard,
  DefList,
  PaymentsView,
  PaymentDetail,
  CreatePaymentForm,
  FundingView,
  DeliveryView,
  FundingLinkView,
  paymentColumns,
};
